import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Box } from '@mui/material';
import useCRM from '../items/hooks/HCRM';
import GContactsTable from '../tables/GContactsTable';

export default function ContactForm({ open, onClose, investorId }: { open: boolean; onClose: () => void; investorId: number | string | null }) {
    const { fetchContactsByInvestor, fetchInvestors } = useCRM();
    const [investorName, setInvestorName] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<any[]>([]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            console.log('ContactForm.load start', { investorId });
            if (!investorId) {
                console.log('ContactForm: no investorId provided, clearing rows');
                setRows([]);
                return;
            }
            setLoading(true);
            setError(null);
            try {
                console.log('ContactForm: calling fetchContactsByInvestor with', investorId);
                const res = await fetchContactsByInvestor(investorId as any);
                console.log('ContactForm: raw response', res);
                const data = Array.isArray(res) ? res : res?.data ?? [];
                console.log('ContactForm: resolved data length', Array.isArray(data) ? data.length : 'not-array');
                if (mounted) {
                    // Remove any Investor id columns from rows for display
                    const cleaned = (Array.isArray(data) ? data : []).map((r: any) => {
                        // remove investor id fields
                        const copy = { ...r };
                        delete copy.Investor_Id;
                        delete copy.InvestorId;
                        delete copy.investor_id;
                        delete copy.investorId;

                        // ensure contact_name (or equivalent) is the first key
                        const keys = Object.keys(copy);
                        const nameKey = keys.find((k) => /contact[_\s]?name/i.test(k))
                            || keys.find((k) => /^name$/i.test(k))
                            || keys.find((k) => /full[_\s]?name/i.test(k));

                        if (nameKey) {
                            const ordered: any = {};
                            ordered[nameKey] = copy[nameKey];
                            for (const k of keys) {
                                if (k === nameKey) continue;
                                ordered[k] = copy[k];
                            }
                            return ordered;
                        }

                        return copy;
                    });
                    setRows(cleaned);

                    // try to infer investor name from the contacts data
                    let inferred: string | null = null;
                    if (cleaned.length > 0) {
                        const first = cleaned[0];
                        const k = Object.keys(first).find((kk) => /investor[_\s]?name/i.test(kk) || /^investor$/i.test(kk) || /company[_\s]?name/i.test(kk) || /company$/i.test(kk));
                        if (k) inferred = String(first[k] ?? '').trim() || null;
                    }

                    if (inferred) {
                        setInvestorName(inferred);
                    } else {
                        // fallback: fetch investors list and try to match by id
                        (async () => {
                            try {
                                const invRes = await fetchInvestors();
                                const invData = Array.isArray(invRes) ? invRes : invRes?.data ?? [];
                                const keys = Object.keys(invData[0] ?? {});
                                const idKey = keys.find((k) => /investor[_\s]?id/i.test(k) || /\bid\b/i.test(k));
                                const nameKey = keys.find((k) => /investor[_\s]?name/i.test(k) || /company[_\s]?name/i.test(k) || /^name$/i.test(k));
                                const found = invData.find((row: any) => {
                                    const val = idKey ? row[idKey] : (row.investor_id ?? row.id ?? row.Investor_id ?? row.InvestorId ?? null);
                                    return String(val) === String(investorId);
                                });
                                if (found) {
                                    const nm = nameKey ? (found[nameKey] ?? '') : (found.name ?? found.full_name ?? '');
                                    setInvestorName(String(nm ?? '').trim() || null);
                                } else {
                                    setInvestorName(null);
                                }
                            } catch (e) {
                                console.warn('ContactForm: failed to fetch investors for name lookup', e);
                                setInvestorName(null);
                            }
                        })();
                    }
                }
            } catch (err: any) {
                console.error('ContactForm: fetch error', err);
                if (mounted) setError(err?.message || 'Failed to load contacts');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        void load();
        return () => { mounted = false; };
    }, [investorId, fetchContactsByInvestor, fetchInvestors]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Contacts for Investor {investorName ?? investorId}</DialogTitle>
            <DialogContent>
                {loading && (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && <Box style={{ color: 'red', padding: 8 }}>{error}</Box>}

                <GContactsTable contacts={rows} loading={loading} error={error} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
